import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';

type UseLongPollingQueryOptions<TData = unknown> = {
	enabled?: boolean;
	onMessage?: (data: TData) => void;
};

export const useLongPollingQuery = <
	TQueryFnData = unknown,
	TError extends AxiosError<any> = AxiosError<any>,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
>(
	queryKey: TQueryKey,
	queryFn: ({ params, signal }: { params: any; page?: any; signal: AbortSignal }) => Promise<TQueryFnData>,
	options?: UseLongPollingQueryOptions<TData>
) => {
	const queryClient = useQueryClient();
	const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		data: _data,
		refetch,
		remove,
		status,
	} = useQuery<TQueryFnData, TError, TData, TQueryKey>(
		queryKey,
		({ pageParam, queryKey, signal }) => queryFn({ params: queryKey[1], page: pageParam, signal: signal! }),
		{
			enabled: false,
			retry: false,
			onSuccess: (data) => {
				if (data) {
					options?.onMessage?.(data);
				}
			},
		}
	);

	const scheduleRetry = useCallback(() => {
		if (retryTimeoutRef.current !== null) return;

		retryTimeoutRef.current = setTimeout(() => {
			retryTimeoutRef.current = null;
			refetch({ throwOnError: false, type: 'all' });
		}, 1000);
	}, [refetch]);

	useEffect(() => {
		if (status === 'error') {
			remove();
			scheduleRetry();
			return;
		}

		const isEmptyResponse = status === 'success' && _data === '';
		if (isEmptyResponse) {
			remove();
			refetch({ throwOnError: false, type: 'all' });
		}
	}, [status, _data, remove, refetch, scheduleRetry]);

	useEffect(() => {
		if (_data) {
			remove();
			refetch({ throwOnError: false, type: 'all' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_data]);

	useEffect(() => {
		if (options?.enabled !== undefined) {
			if (options?.enabled === false) {
				remove();
			} else {
				refetch({ throwOnError: false, type: 'all' });
			}
		} else {
			refetch();
		}
		return () => {
			if (retryTimeoutRef.current !== null) {
				clearTimeout(retryTimeoutRef.current);
				retryTimeoutRef.current = null;
			}
			remove();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options?.enabled]);

	const cancel = useCallback(() => {
		if (retryTimeoutRef.current !== null) {
			clearTimeout(retryTimeoutRef.current);
			retryTimeoutRef.current = null;
		}
		queryClient.cancelQueries(queryKey);
		remove();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey]);

	const data = _data === '' ? null : _data;

	return { data, cancel };
};
